import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

export type NotificationVariant = 'default' | 'success' | 'error' | 'warning'

interface NotificationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  variant?: NotificationVariant
}

const variantConfig = {
  default: {
    icon: Info,
    iconClass: 'text-blue-500',
    titleClass: '',
  },
  success: {
    icon: CheckCircle2,
    iconClass: 'text-green-500',
    titleClass: 'text-green-700 dark:text-green-400',
  },
  error: {
    icon: XCircle,
    iconClass: 'text-red-500',
    titleClass: 'text-red-700 dark:text-red-400',
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-yellow-500',
    titleClass: 'text-yellow-700 dark:text-yellow-400',
  },
}

export function NotificationDialog({
  open,
  onOpenChange,
  title,
  description,
  variant = 'default',
}: NotificationDialogProps) {
  const config = variantConfig[variant]
  const Icon = config.icon

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Icon className={cn('h-6 w-6', config.iconClass)} />
            <DialogTitle className={config.titleClass}>{title}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>OK</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Helper hook for managing notification state
export interface NotificationState {
  open: boolean
  title: string
  description: string
  variant: NotificationVariant
}

export const initialNotificationState: NotificationState = {
  open: false,
  title: '',
  description: '',
  variant: 'default',
}

export function showNotification(
  setState: React.Dispatch<React.SetStateAction<NotificationState>>,
  title: string,
  description: string,
  variant: NotificationVariant = 'default'
) {
  setState({ open: true, title, description, variant })
}
